import { NodeSingular } from 'cytoscape';

const DEFAULT_MIN_WIDTH = 60;
const TEXT_PADDING_BUFFER = 12;
const AUTO_LABEL_WIDTH_SCRATCH_KEY = '__opmcvAutoLabelWidthKey';

// 缓存：避免在 updateFromMasterModel 中反复测量同样的文本
const autoLabelStyleCache = new Map<
  string,
  { font: string; padding: number; minWidth: number }
>();
const textWidthCache = new Map<string, number>();
const autoLabelWidthCache = new Map<string, number>();
const MAX_TEXT_WIDTH_CACHE_SIZE = 50000;

let measureContext: CanvasRenderingContext2D | null = null;

const ensureMeasureContext = () => {
  if (measureContext || typeof document === 'undefined') {
    return measureContext;
  }
  const canvas = document.createElement('canvas');
  measureContext = canvas.getContext('2d');
  return measureContext;
};

const parseNumericStyle = (value?: string | number | null) => {
  if (value === undefined || value === null) {
    return NaN;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? NaN : parsed;
};

const getHorizontalPadding = (node: NodeSingular) => {
  const paddingLeft = parseNumericStyle(node.style('padding-left'));
  const paddingRight = parseNumericStyle(node.style('padding-right'));
  if (!Number.isNaN(paddingLeft) && !Number.isNaN(paddingRight)) {
    return paddingLeft + paddingRight;
  }
  const padding = parseNumericStyle(node.style('padding'));
  if (!Number.isNaN(padding)) {
    return padding * 2;
  }
  return 0;
};

const buildFontString = (node: NodeSingular) => {
  const fontStyle = node.style('font-style') || 'normal';
  const fontWeight = node.style('font-weight') || 'normal';
  const fontSize = parseNumericStyle(node.style('font-size')) || 14;
  const fontFamily = node.style('font-family') || 'sans-serif';
  return `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
};

const measureTextWidth = (label: string, node: NodeSingular) => {
  const context = ensureMeasureContext();
  if (!context) {
    return Math.max(label.length * 12, DEFAULT_MIN_WIDTH);
  }
  context.font = buildFontString(node);
  const metrics = context.measureText(label);
  return metrics.width;
};

const measureTextWidthWithFont = (label: string, font: string) => {
  const context = ensureMeasureContext();
  if (!context) {
    return Math.max(label.length * 12, DEFAULT_MIN_WIDTH);
  }
  context.font = font;
  const metrics = context.measureText(label);
  return metrics.width;
};

const getAutoLabelStyleKey = (node: NodeSingular) => {
  const type = node.data('type') ?? '';
  const isParent =
    typeof node.isParent === 'function' ? node.isParent() : false;
  const hasParent = Boolean(node.data('parent'));
  // 需要区分 compound 子节点（$node > node[...]）与普通节点，否则 padding/min-width 可能不同
  return `${type}|${isParent ? 'parent' : 'leaf'}|${hasParent ? 'child' : 'root'}`;
};

const getAutoLabelStyle = (node: NodeSingular) => {
  const key = getAutoLabelStyleKey(node);
  const cached = autoLabelStyleCache.get(key);
  if (cached) return cached;

  const font = buildFontString(node);
  const padding = getHorizontalPadding(node);
  const minWidth = Math.max(
    DEFAULT_MIN_WIDTH,
    parseNumericStyle(node.style('min-width')) || 0
  );
  const style = { font, padding, minWidth };
  autoLabelStyleCache.set(key, style);
  return style;
};

const getAutoLabelWidthKey = (node: NodeSingular, label?: string) => {
  const text = label ?? node.data('label') ?? node.data('MMRef')?.label ?? '';
  const { font, padding, minWidth } = getAutoLabelStyle(node);
  // key 必须包含字体与 padding/minWidth，避免不同样式下复用错误宽度
  const key = `${font}|${padding}|${minWidth}|${text}`;
  return { key, text, font, padding, minWidth };
};

export const computeAutoLabelWidth = (node: NodeSingular, label?: string) => {
  const { key, text, font, padding, minWidth } = getAutoLabelWidthKey(
    node,
    label
  );
  const cachedWidth = autoLabelWidthCache.get(key);
  if (cachedWidth !== undefined) {
    return cachedWidth;
  }

  const textKey = `${font}|${text}`;
  let measuredWidth = textWidthCache.get(textKey);
  if (measuredWidth === undefined) {
    measuredWidth = measureTextWidthWithFont(text, font);
    if (textWidthCache.size > MAX_TEXT_WIDTH_CACHE_SIZE) {
      textWidthCache.clear();
    }
    textWidthCache.set(textKey, measuredWidth);
  }

  const width = Math.ceil(
    Math.max(measuredWidth + padding + TEXT_PADDING_BUFFER, minWidth)
  );
  autoLabelWidthCache.set(key, width);
  return width;
};

export const applyAutoLabelWidth = (node: NodeSingular, label?: string) => {
  const { key } = getAutoLabelWidthKey(node, label);
  const lastKey = node.scratch(AUTO_LABEL_WIDTH_SCRATCH_KEY);
  if (lastKey === key) {
    const currentWidth = node.data('labelWidth');
    if (typeof currentWidth === 'number' && Number.isFinite(currentWidth)) {
      return currentWidth;
    }
  }

  const width = computeAutoLabelWidth(node, label);
  node.data({ labelWidth: width });
  node.scratch(AUTO_LABEL_WIDTH_SCRATCH_KEY, key);
  return width;
};
